import usePrevious from 'hooks/usePrevious';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ADD_DELETE_REQUEST, REMOVE_DELETE_REQUEST, UPDATE_APPLICATION_DETAIL_POST_DATA } from 'store/key';
import { generateRandomId, produce } from 'utility/general';

const useSourceFundingData = ({ formId = -1 }) => {
    const [openPopup, setOpenPopup] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const serverTableKey = 'funding';
    const remoteData = useSelector((state) => state.applicationDetail?.[formId]?.[serverTableKey]);
    const updatePostData = useSelector((state) => state.applicationDetailPostData.postData[serverTableKey]);
    const dispatch = useDispatch();
    const [rows, setRows] = useState(remoteData ?? []);
    const previousData = usePrevious(remoteData);

    // update row when remote data update
    useEffect(() => {
        if (!isEqual(previousData, remoteData)) {
            setRows(remoteData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remoteData]);

    const removeAllTempData = () => {
        setRows(
            rows.filter((item) => {
                return !item.id.toString().startsWith('temp_');
            }),
        );
    };

    const [choice] = useState({
        type: [
            { key: 0, label: 'Donation' },
            { key: 1, label: 'Hospital / Department funding' },
            { key: 2, label: 'Local research grant' },
            { key: 3, label: 'Overseas research grant' },
            { key: 4, label: 'University funding' },
            { key: 5, label: 'Others' },
        ],
    });

    const getChoiceLabel = (type, key) => {
        return (
            choice[type]?.find((item) => {
                return item.key === key;
            })?.label ?? '-'
        );
    };

    const [defaultFormList] = useState({
        commercial: {
            value: '',
            label: 'Commercial / Non-commercial',
            name: 'commercial',
            type: 'boolean',
            trueValue: 'Commercial',
            falseValue: 'Non-commercial',
            isRequired: true,
        },
        type: {
            value: '',
            label: 'Type of funding',
            name: 'type',
            type: 'select',
            operators: choice['type'],
            validators: ['isRequired'],
            isDisabled: (formList) => {
                const disabled = formList.commercial.value ? true : false;
                if (disabled) {
                    formList.type.value = '';
                    formList.type.validators = [];
                } else {
                    formList.type.validators = ['isRequired'];
                }
                return disabled;
            },
        },
        name: {
            value: '',
            label: 'Name of organization / party',
            name: 'name',
            type: 'input',
            validators: ['isRequired'],
            maxLength: 255,
        },
        ownership: {
            value: '',
            label: 'Study data Ownership',
            name: 'ownership',
            type: 'boolean',
            isRequired: true,
        },
        majorRight: {
            value: '',
            label: 'Major Right and Responsibility of the organization / party in this study',
            name: 'majorRight',
            type: 'input',
            validators: ['isRequired'],
            maxLength: 255,
            isTextArea: true,
            textAreaMinRow: 3,
            labelTooltip: (
                <div>
                    <div>E.g.</div>
                    <div>- Intellectual property right</div>
                    <div>- Developing and manufacturing investigational product</div>
                    <div>- Designing the clinical study</div>
                    <div>- Developing the study protocol</div>
                    <div>- Quality management</div>
                    <div>- Data analysis</div>
                    <div>- Reporting</div>
                    <div>- Regulatory responsibility</div>
                    <div>- Legal responsibility</div>
                    <div>- Financing the study</div>
                </div>
            ),
        },
    });

    const getLabelName = (key) => {
        return defaultFormList[key]?.label ?? 'undefined';
    };

    const recoverDataCreation = async (item) => {
        // handle edited data recovery
        const updatedPostData = [];

        rows.forEach((item) => {
            const remoteDataObject = remoteData.find((remoteItem) => {
                return remoteItem.id === item.id;
            });

            if (remoteDataObject) {
                const isSame = Object.entries(item).every(([key, value]) => {
                    // remove exception
                    if (key === 'siteInfo' || key === 'applicationForm') {
                        return true;
                    }

                    return remoteDataObject[key] === value;
                });

                if (!isSame) {
                    /** delete type when type==='' */
                    if (item.type === '') {
                        // delete item.type;
                        item.type = null;
                    }
                    updatedPostData.push(item);
                }
            }
        });

        const postData = rows.filter((row) => {
            const isDataCreation = row.id.toString().startsWith('temp_');

            if (!isDataCreation) {
                dispatch({
                    type: REMOVE_DELETE_REQUEST,
                    payload: {
                        type: serverTableKey,
                        id: row.id,
                        applicationFormId: formId,
                    },
                });
            }

            return isDataCreation;
        });

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [...postData, ...updatedPostData],
                applicationFormId: formId,
            },
        });
    };

    const onDataEmpty = async (item) => {
        remoteData?.forEach((row) => {
            dispatch({
                type: ADD_DELETE_REQUEST,
                payload: {
                    type: serverTableKey,
                    id: row.id,
                    applicationFormId: formId,
                },
            });
        });

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [],
                applicationFormId: formId,
            },
        });
    };

    const handleEditClick = (row) => {
        setSelectedRow(row);
        setOpenPopup(true);
    };

    const handleRowCreate = async (id) => {
        setSelectedRow(null);
        setOpenPopup(true);
    };

    const handleDelete = async (row) => {
        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                if (index !== -1) {
                    draft.splice(index, 1);
                }
            }),
        );

        // skip if id starts with temp_, e.g. not exist in server
        if (row.id.toString().startsWith('temp_')) {
            let postData = updatePostData ?? [];

            const index = postData.findIndex((item) => {
                return item.id === row.id;
            });

            if (index !== -1) {
                postData.splice(index, 1);
            }

            dispatch({
                type: UPDATE_APPLICATION_DETAIL_POST_DATA,
                payload: {
                    type: serverTableKey,
                    data: postData,
                    applicationFormId: formId,
                },
            });

            return;
        }

        dispatch({
            type: ADD_DELETE_REQUEST,
            payload: {
                type: serverTableKey,
                id: row.id,
                applicationFormId: formId,
            },
        });
    };

    const onRecordUpdate = async (data) => {
        data.id = `temp_${generateRandomId()}`;
        const newRows = produce(rows, (draft) => {
            draft.push(data);
        });
        setRows(newRows);

        /** delete type when type==='' */
        if (data.type === '') {
            // delete data.type;
            data.type = null;
        }

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [...(updatePostData ?? []), data],
                applicationFormId: formId,
            },
        });
    };

    const onEditSubmit = (row) => {
        let postData = updatePostData ?? [];

        const index = postData.findIndex((item) => {
            return item.id === row.id;
        });

        /** delete type when type==='' */
        if (row.type === '') {
            // delete row.type;
            row.type = null;
        }
        if (index === -1) {
            postData.push(row);
        } else {
            postData[index] = row;
        }

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: postData,
                applicationFormId: formId,
            },
        });

        //update local data
        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                draft[index] = row;
            }),
        );
    };

    const [columns] = useState([
        {
            field: 'commercial',
            headerName: getLabelName('commercial'),
            width: 150,
            valueGetter: (params) => {
                if (params.value) {
                    return 'Commercial';
                } else {
                    return 'Non-commercial';
                }
            },
        },
        {
            field: 'type',
            headerName: getLabelName('type'),
            width: 150,
            valueGetter: (params) => {
                return getChoiceLabel('type', params.value);
            },
        },
        { field: 'name', headerName: getLabelName('name'), width: 200 },
        {
            field: 'ownership',
            headerName: getLabelName('ownership'),
            width: 150,
            tooltip: 'Does the aforementioned organization/party has ownership of the study data?',
        },
        {
            field: 'majorRight',
            headerName: getLabelName('majorRight'),
            width: 300,
            tooltip: (
                <div>
                    <div>E.g.</div>
                    <div>- Intellectual property right</div>
                    <div>- Developing and manufacturing investigational product</div>
                    <div>- Designing the clinical study</div>
                    <div>- Developing the study protocol</div>
                    <div>- Quality management</div>
                    <div>- Data analysis</div>
                    <div>- Reporting</div>
                    <div>- Regulatory responsibility</div>
                    <div>- Legal responsibility</div>
                    <div>- Financing the study</div>
                </div>
            ),
        },
    ]);

    return {
        columns,
        rows,
        openPopup,
        setOpenPopup,
        defaultFormList,
        selectedRow,
        handleRowCreate,
        onRecordUpdate,
        handleEditClick,
        setRows,
        handleDelete,
        onEditSubmit,
        recoverDataCreation,
        onDataEmpty,
        removeAllTempData,
    };
};

export default useSourceFundingData;
